<section class="hotspot-passengers-page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Hotspot passengers</p>
      <h2>Powerlynx passenger records</h2>
      <p>Review hotspot passenger records and manually create or update local passenger profiles.</p>
    </div>
    <div class="future-hooks" aria-label="Planned hotspot sync hooks">
      <span><i class="material-icons">fact_check</i>Batch sync selected</span>
      <span><i class="material-icons">sync</i>Sync visible results</span>
      <span><i class="material-icons">join_inner</i>Duplicate confidence</span>
    </div>
  </div>

  <div class="alert success" *ngIf="successMessage">
    <i class="material-icons">check_circle</i>
    <span>{{ successMessage }}</span>
  </div>

  <section class="session-sync-panel">
    <div class="sync-tabs" role="tablist" aria-label="Hotspot sync and maintenance sections">
      <button type="button" [class.active]="activeSyncTab === 'passengers'" (click)="setSyncTab('passengers')">
        <i class="material-icons">groups</i>
        <span>Passengers</span>
      </button>
      <button type="button" [class.active]="activeSyncTab === 'sessions'" (click)="setSyncTab('sessions')">
        <i class="material-icons">sync_alt</i>
        <span>Sessions</span>
      </button>
      <button type="button" [class.active]="activeSyncTab === 'maintenance'" (click)="setSyncTab('maintenance')">
        <i class="material-icons">build</i>
        <span>Maintenance</span>
      </button>
      <button type="button" [class.active]="activeSyncTab === 'history'" (click)="setSyncTab('history')">
        <i class="material-icons">history</i>
        <span>History</span>
      </button>
    </div>

    <div class="operator-note" *ngIf="!isAdminOrPlatform">
      <i class="material-icons">info</i>
      <span>Hotspot sync and maintenance actions are available to admins and platform employees.</span>
    </div>

    <section class="sync-tab-panel" *ngIf="activeSyncTab === 'passengers'">
      <div class="panel-toolbar">
        <div>
          <p class="eyebrow">Passenger sync</p>
          <h3>Sync Powerlynx customers</h3>
          <p>Sync Powerlynx customers into local passenger profiles before syncing sessions, so sessions can link to passengers.</p>
        </div>
        <div class="session-controls">
          <label>Page <input type="number" min="1" [(ngModel)]="passengerSyncPage" [disabled]="isPassengerBatchSyncing || !isAdminOrPlatform"></label>
          <label>Per page <input type="number" min="1" max="1000" [(ngModel)]="passengerSyncPerPage" [disabled]="isPassengerBatchSyncing || !isAdminOrPlatform"></label>
          <label>Search <input type="text" [(ngModel)]="passengerSyncSearch" [disabled]="isPassengerBatchSyncing || !isAdminOrPlatform"></label>
          <label>Sort by <input type="text" [(ngModel)]="passengerSyncSortBy" [disabled]="isPassengerBatchSyncing || !isAdminOrPlatform"></label>
          <label>Direction
            <select [(ngModel)]="passengerSyncSortDirection" [disabled]="isPassengerBatchSyncing || !isAdminOrPlatform">
              <option value="desc">desc</option>
              <option value="asc">asc</option>
            </select>
          </label>
          <button class="primary-action" type="button" [disabled]="isPassengerBatchSyncing || !isAdminOrPlatform" (click)="syncPassengerProfiles()">
            <i class="material-icons">{{ isPassengerBatchSyncing ? 'sync' : 'cloud_sync' }}</i>
            <span>{{ isPassengerBatchSyncing ? 'Syncing...' : 'Sync passengers' }}</span>
          </button>
        </div>
      </div>

      <div class="inline-error detail-error" *ngIf="passengerBatchSyncError">{{ passengerBatchSyncError }}</div>
      <div class="session-loading" *ngIf="isPassengerBatchSyncing"><i class="material-icons">sync</i><span>Syncing Powerlynx customers...</span></div>

      <section class="session-result" *ngIf="passengerBatchSyncResult as result">
        <div class="metric-strip">
          <span><small>Processed</small><strong>{{ result.processed }}</strong></span>
          <span><small>Profiles</small><strong>{{ result.createdProfiles }}</strong></span>
          <span><small>External links</small><strong>{{ result.createdExternalIdentities }}</strong></span>
          <span><small>Errors</small><strong>{{ getErrorCount(result.errors) }}</strong></span>
        </div>
        <div class="result-meta">
          <span class="status-badge">page {{ result.requestedPage }}</span>
          <span class="status-badge">per page {{ result.requestedPerPage }}</span>
          <span class="status-badge provider" *ngIf="result.hotspotSyncRunID">Run {{ result.hotspotSyncRunID }}</span>
        </div>
        <div class="error-toggle" *ngIf="result.errors.length">
          <button class="btn-icon-text" type="button" (click)="showPassengerBatchErrors = !showPassengerBatchErrors">
            <i class="material-icons">{{ showPassengerBatchErrors ? 'expand_less' : 'expand_more' }}</i>
            <span>{{ showPassengerBatchErrors ? 'Hide errors' : 'Show errors' }}</span>
          </button>
        </div>
        <div class="session-errors" *ngIf="showPassengerBatchErrors && result.errors.length">
          <div class="session-error-row" *ngFor="let error of result.errors; trackBy: trackSessionError">
            <strong>{{ error.externalID || 'Passenger sync error' }}</strong>
            <span>{{ error.message || error.detail || 'No error detail returned.' }}</span>
          </div>
        </div>
      </section>
    </section>

    <section class="sync-tab-panel" *ngIf="activeSyncTab === 'sessions'">
      <div class="maintenance-grid">
        <article class="maintenance-card">
          <div>
            <p class="eyebrow">Session sync</p>
            <h3>Sync sessions from offset</h3>
            <p>Offset 0 reads older Powerlynx records. Newer records may be at higher offsets. Scheduled cursor sync is better for ongoing imports after catch-up.</p>
          </div>
          <div class="session-controls">
            <label>Limit <input type="number" min="1" max="1000" [(ngModel)]="sessionLimit" [disabled]="isSessionSyncing || !isAdminOrPlatform"></label>
            <label>Offset <input type="number" min="0" [(ngModel)]="sessionOffset" [disabled]="isSessionSyncing || !isAdminOrPlatform"></label>
            <button class="primary-action" type="button" [disabled]="isSessionSyncing || !isAdminOrPlatform" (click)="syncSessions()">
              <i class="material-icons">{{ isSessionSyncing ? 'sync' : 'cloud_sync' }}</i>
              <span>{{ isSessionSyncing ? 'Syncing...' : 'Sync sessions from offset' }}</span>
            </button>
          </div>
          <div class="inline-error" *ngIf="sessionSyncError">{{ sessionSyncError }}</div>
        </article>

        <article class="maintenance-card">
          <div>
            <p class="eyebrow">Catch-up</p>
            <h3>Run session catch-up</h3>
            <p>Use for larger backfills. Leaving max batches empty may run until the provider returns no more sessions.</p>
          </div>
          <div class="session-controls">
            <label>Limit <input type="number" min="1" max="1000" [(ngModel)]="catchUpLimit" [disabled]="isCatchUpSyncing || !isAdminOrPlatform"></label>
            <label>Start offset <input type="number" min="0" [(ngModel)]="catchUpStartOffset" [disabled]="isCatchUpSyncing || !isAdminOrPlatform"></label>
            <label>Max batches <input type="number" min="1" [(ngModel)]="catchUpMaxBatches" [disabled]="isCatchUpSyncing || !isAdminOrPlatform"></label>
            <button class="primary-action" type="button" [disabled]="isCatchUpSyncing || !isAdminOrPlatform" (click)="syncAllSessions()">
              <i class="material-icons">{{ isCatchUpSyncing ? 'sync' : 'cloud_sync' }}</i>
              <span>{{ isCatchUpSyncing ? 'Running...' : 'Run session catch-up' }}</span>
            </button>
          </div>
          <div class="inline-error" *ngIf="catchUpError">{{ catchUpError }}</div>
        </article>
      </div>

      <div class="session-loading" *ngIf="isSessionSyncing || isCatchUpSyncing"><i class="material-icons">sync</i><span>Running session sync...</span></div>

      <section class="session-result" *ngIf="sessionSyncResult as result">
        <div class="metric-strip">
          <span><small>Processed</small><strong>{{ result.processed }}</strong></span>
          <span><small>New sessions</small><strong>{{ result.createdSessions }}</strong></span>
          <span><small>New mappings</small><strong>{{ result.createdAssetMappings }}</strong></span>
          <span><small>Errors</small><strong>{{ result.errors.length }}</strong></span>
        </div>
        <div class="result-meta">
          <span class="status-badge provider">Run {{ result.hotspotSyncRunID }}</span>
          <span class="status-badge">limit {{ result.requestedLimit }}</span>
          <span class="status-badge">offset {{ result.requestedOffset }}</span>
          <span class="status-badge">{{ getSessionResultSummary(result) }}</span>
        </div>
        <div class="table-scroll compact-table" *ngIf="result.results.length">
          <table>
            <thead>
              <tr>
                <th>External ID</th>
                <th>Session ID</th>
                <th>Profile ID</th>
                <th>Mapping ID</th>
                <th>Router identity</th>
                <th>Organisation ID</th>
                <th>Vehicle ID</th>
                <th>Session</th>
                <th>Mapping</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of getPagedSessionResults(result); trackBy: trackSessionResult">
                <td><strong>{{ row.externalID }}</strong></td>
                <td>{{ row.hotspotSessionRecordID || '-' }}</td>
                <td>{{ row.passengerProfileID || '-' }}</td>
                <td>{{ row.hotspotAssetMappingID || '-' }}</td>
                <td>{{ row.nasIdentifier || '-' }}</td>
                <td>{{ row.organisationID || '-' }}</td>
                <td>{{ row.vehicleID || '-' }}</td>
                <td><span class="status-badge" [class.created]="row.createdSession">{{ row.createdSession ? 'created' : 'existing' }}</span></td>
                <td><span class="status-badge" [class.created]="row.createdAssetMapping">{{ row.createdAssetMapping ? 'created' : 'existing' }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pagination-bar compact-pagination" *ngIf="result.results.length > sessionResultPageSize">
          <button class="btn-icon-text" type="button" [disabled]="sessionResultPage === 1" (click)="changeSessionResultPage(sessionResultPage - 1, result)"><i class="material-icons">chevron_left</i><span>Previous</span></button>
          <span>Page {{ sessionResultPage }} of {{ getSessionResultTotalPages(result) }}</span>
          <button class="btn-icon-text" type="button" [disabled]="sessionResultPage === getSessionResultTotalPages(result)" (click)="changeSessionResultPage(sessionResultPage + 1, result)"><span>Next</span><i class="material-icons">chevron_right</i></button>
        </div>
        <div class="error-toggle" *ngIf="result.errors.length">
          <button class="btn-icon-text" type="button" (click)="showSessionErrors = !showSessionErrors"><i class="material-icons">{{ showSessionErrors ? 'expand_less' : 'expand_more' }}</i><span>{{ showSessionErrors ? 'Hide errors' : 'Show errors' }}</span></button>
        </div>
        <div class="session-errors" *ngIf="showSessionErrors && result.errors.length">
          <div class="session-error-row" *ngFor="let error of result.errors; trackBy: trackSessionError">
            <strong>{{ error.externalID || 'Unknown external ID' }}</strong>
            <span>{{ error.message || error.detail || 'No error detail returned.' }}</span>
          </div>
        </div>
      </section>

      <section class="session-result" *ngIf="catchUpResult as result">
        <div class="metric-strip wide-strip">
          <span><small>Batches</small><strong>{{ result.batchesProcessed }}</strong></span>
          <span><small>Processed</small><strong>{{ result.processed }}</strong></span>
          <span><small>New sessions</small><strong>{{ result.createdSessions }}</strong></span>
          <span><small>New mappings</small><strong>{{ result.createdAssetMappings }}</strong></span>
          <span><small>Errors</small><strong>{{ getErrorCount(result.errors) }}</strong></span>
        </div>
        <div class="result-meta">
          <span class="status-badge">limit {{ result.requestedLimit }}</span>
          <span class="status-badge">start offset {{ result.startOffset }}</span>
          <span class="status-badge">next offset {{ result.nextOffset ?? '-' }}</span>
          <span class="status-badge">max batches {{ result.maxBatches ?? '-' }}</span>
        </div>
        <div class="error-toggle" *ngIf="result.errors.length">
          <button class="btn-icon-text" type="button" (click)="showCatchUpErrors = !showCatchUpErrors"><i class="material-icons">{{ showCatchUpErrors ? 'expand_less' : 'expand_more' }}</i><span>{{ showCatchUpErrors ? 'Hide errors' : 'Show errors' }}</span></button>
        </div>
        <div class="session-errors" *ngIf="showCatchUpErrors && result.errors.length">
          <div class="session-error-row" *ngFor="let error of result.errors; trackBy: trackSessionError">
            <strong>{{ error.externalID || 'Catch-up error' }}</strong>
            <span>{{ error.message || error.detail || 'No error detail returned.' }}</span>
          </div>
        </div>
      </section>
    </section>

    <section class="sync-tab-panel" *ngIf="activeSyncTab === 'maintenance'">
      <div class="workflow-note">
        <span>1. Sync passengers/customers</span>
        <span>2. Sync sessions</span>
        <span>3. Remap sessions</span>
        <span>4. Relink profiles</span>
        <span>5. Review mappings</span>
      </div>
      <div class="maintenance-grid">
        <article class="maintenance-card">
          <div>
            <p class="eyebrow">Asset remap</p>
            <h3>Remap existing sessions</h3>
            <p>Re-resolves existing session records to organisations and vehicles using Powerlynx location and hotspot mappings.</p>
          </div>
          <div class="quick-limits">
            <button type="button" class="btn-icon-text" *ngFor="let limit of maintenanceLimitOptions" (click)="maintenanceLimit = limit">{{ limit }}</button>
          </div>
          <div class="session-controls">
            <label>Limit <input type="number" min="1" [(ngModel)]="maintenanceLimit" [disabled]="isRemapping || !isAdminOrPlatform"></label>
            <button class="primary-action" type="button" [disabled]="isRemapping || !isAdminOrPlatform" (click)="remapExistingSessions()"><i class="material-icons">{{ isRemapping ? 'sync' : 'manage_history' }}</i><span>{{ isRemapping ? 'Remapping...' : 'Remap existing sessions' }}</span></button>
          </div>
          <div class="inline-error" *ngIf="remapError">{{ remapError }}</div>
          <div class="metric-strip" *ngIf="remapResult as result">
            <span><small>Reviewed</small><strong>{{ result.reviewed }}</strong></span>
            <span><small>Remapped</small><strong>{{ result.remappedSessions }}</strong></span>
            <span><small>Mappings</small><strong>{{ result.createdAssetMappings }}</strong></span>
            <span><small>Errors</small><strong>{{ result.errors }}</strong></span>
          </div>
          <div class="error-toggle" *ngIf="remapResult?.errorDetails?.length">
            <button class="btn-icon-text" type="button" (click)="showRemapErrors = !showRemapErrors"><i class="material-icons">{{ showRemapErrors ? 'expand_less' : 'expand_more' }}</i><span>{{ showRemapErrors ? 'Hide errors' : 'Show errors' }}</span></button>
          </div>
          <div class="session-errors" *ngIf="showRemapErrors && remapResult?.errorDetails?.length">
            <div class="session-error-row" *ngFor="let error of remapResult?.errorDetails; trackBy: trackSessionError"><strong>{{ error.externalID || 'Remap error' }}</strong><span>{{ error.message || error.detail || 'No error detail returned.' }}</span></div>
          </div>
        </article>

        <article class="maintenance-card">
          <div>
            <p class="eyebrow">Passenger relink</p>
            <h3>Relink passenger profiles</h3>
            <p>Links existing unlinked session records to local passenger profiles. Run after syncing passengers/customers.</p>
          </div>
          <div class="quick-limits">
            <button type="button" class="btn-icon-text" *ngFor="let limit of maintenanceLimitOptions" (click)="maintenanceLimit = limit">{{ limit }}</button>
          </div>
          <div class="session-controls">
            <label>Limit <input type="number" min="1" [(ngModel)]="maintenanceLimit" [disabled]="isRelinking || !isAdminOrPlatform"></label>
            <button class="primary-action" type="button" [disabled]="isRelinking || !isAdminOrPlatform" (click)="relinkPassengerProfiles()"><i class="material-icons">{{ isRelinking ? 'sync' : 'link' }}</i><span>{{ isRelinking ? 'Relinking...' : 'Relink passenger profiles' }}</span></button>
          </div>
          <div class="inline-error" *ngIf="relinkError">{{ relinkError }}</div>
          <div class="metric-strip wide-strip" *ngIf="relinkResult as result">
            <span><small>Reviewed</small><strong>{{ result.reviewed }}</strong></span>
            <span><small>Relinked</small><strong>{{ result.relinkedSessions }}</strong></span>
            <span><small>Already linked</small><strong>{{ result.alreadyLinked }}</strong></span>
            <span><small>Unmatched</small><strong>{{ result.unmatched }}</strong></span>
            <span><small>Errors</small><strong>{{ result.errors }}</strong></span>
          </div>
          <div class="error-toggle" *ngIf="relinkResult?.errorDetails?.length">
            <button class="btn-icon-text" type="button" (click)="showRelinkErrors = !showRelinkErrors"><i class="material-icons">{{ showRelinkErrors ? 'expand_less' : 'expand_more' }}</i><span>{{ showRelinkErrors ? 'Hide errors' : 'Show errors' }}</span></button>
          </div>
          <div class="session-errors" *ngIf="showRelinkErrors && relinkResult?.errorDetails?.length">
            <div class="session-error-row" *ngFor="let error of relinkResult?.errorDetails; trackBy: trackSessionError"><strong>{{ error.externalID || 'Relink error' }}</strong><span>{{ error.message || error.detail || 'No error detail returned.' }}</span></div>
          </div>
        </article>
      </div>
    </section>

    <section class="sync-history sync-tab-panel" *ngIf="activeSyncTab === 'history'">
      <div class="history-header">
        <div>
          <p class="eyebrow">History</p>
          <h3>Hotspot sync runs</h3>
        </div>
        <div class="history-actions">
          <select [(ngModel)]="sessionHistoryResource" (ngModelChange)="onHistoryResourceChanged()">
            <option value="">All resources</option>
            <option value="Passengers">Passengers</option>
            <option value="Sessions">Sessions</option>
          </select>
          <button class="btn-icon-text" type="button" [disabled]="isLoadingSessionHistory" (click)="loadSessionHistory()"><i class="material-icons">refresh</i><span>Refresh</span></button>
        </div>
      </div>
      <div class="inline-error detail-error" *ngIf="sessionHistoryError">{{ sessionHistoryError }}</div>
      <div class="session-loading" *ngIf="isLoadingSessionHistory"><i class="material-icons">sync</i><span>Loading sync history...</span></div>
      <div class="table-scroll compact-table" *ngIf="!isLoadingSessionHistory && sessionHistory.length">
        <table>
          <thead>
            <tr>
              <th>Run ID</th>
              <th>Resource</th>
              <th>Type</th>
              <th>Status</th>
              <th>Processed</th>
              <th>Profiles</th>
              <th>Sessions</th>
              <th>Mappings</th>
              <th>Errors</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let run of sessionHistory; trackBy: trackSessionRun">
              <td><strong>{{ getSessionRunId(run) }}</strong></td>
              <td>{{ run.resource || '-' }}</td>
              <td>{{ run.syncType || '-' }}</td>
              <td><span class="status-badge">{{ run.status || '-' }}</span></td>
              <td>{{ run.processed ?? '-' }}</td>
              <td>{{ run.createdProfiles ?? run.createdPassengerProfiles ?? '-' }}</td>
              <td>{{ run.createdSessions ?? '-' }}</td>
              <td>{{ run.createdAssetMappings ?? '-' }}</td>
              <td>{{ getRunErrorCount(run) }}</td>
              <td>{{ getSessionRunTime(run) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="empty-state compact-empty" *ngIf="!isLoadingSessionHistory && !sessionHistory.length && !sessionHistoryError">
        <i class="material-icons">history</i>
        <h3>No sync runs yet</h3>
        <p>Sync history will appear after the backend records hotspot sync runs.</p>
      </div>
      <div class="pagination-bar" *ngIf="!isLoadingSessionHistory && sessionHistoryTotalPages > 1">
        <button class="btn-icon-text" type="button" [disabled]="sessionHistoryPage === 1" (click)="changeSessionHistoryPage(sessionHistoryPage - 1)"><i class="material-icons">chevron_left</i><span>Previous</span></button>
        <span>Page {{ sessionHistoryPage }} of {{ sessionHistoryTotalPages }}</span>
        <button class="btn-icon-text" type="button" [disabled]="sessionHistoryPage === sessionHistoryTotalPages" (click)="changeSessionHistoryPage(sessionHistoryPage + 1)"><span>Next</span><i class="material-icons">chevron_right</i></button>
      </div>
    </section>
  </section>

  <div class="workspace-grid">
    <section class="passengers-panel">
      <div class="panel-toolbar">
        <div>
          <p class="eyebrow">Passenger list</p>
          <h3>{{ getPassengerPageSummary() }}</h3>
        </div>
        <div class="passenger-actions">
          <label class="search-box">
            <i class="material-icons">search</i>
            <input type="search" placeholder="Search hotspot passengers" [(ngModel)]="searchTerm" (ngModelChange)="onPassengerSearchChanged()">
          </label>
          <button class="btn-icon-text" type="button" [disabled]="isLoadingPassengers" (click)="loadPassengers()">
            <i class="material-icons">refresh</i>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div class="inline-error detail-error" *ngIf="passengerListError">{{ passengerListError }}</div>

      <div class="session-loading" *ngIf="isLoadingPassengers">
        <i class="material-icons">sync</i>
        <span>Loading live hotspot passengers...</span>
      </div>

      <div class="table-scroll" *ngIf="!isLoadingPassengers && filteredPassengers.length; else noPassengers">
        <table>
          <thead>
            <tr>
              <th>Passenger</th>
              <th>Provider</th>
              <th>External ID</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Online</th>
              <th>Last seen</th>
              <th>Profile</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let passenger of getPagedPassengers(); trackBy: trackPassenger"
                [class.selected]="selectedPassenger?.id === passenger.id"
                (click)="openPassenger(passenger)">
              <td>
                <strong>{{ passenger.displayName }}</strong>
                <small>{{ passenger.packageName || 'No package recorded' }}</small>
                <div class="inline-error" *ngIf="syncErrors[passenger.id]">{{ syncErrors[passenger.id] }}</div>
              </td>
              <td><span class="status-badge provider">{{ passenger.provider }}</span></td>
              <td>{{ passenger.externalId }}</td>
              <td>
                <span>{{ passenger.phone || '-' }}</span>
                <small>{{ passenger.email || '-' }}</small>
              </td>
              <td><span class="status-badge">{{ passenger.status }}</span></td>
              <td><span class="status-badge" [ngClass]="'online-' + passenger.onlineState.toLowerCase()">{{ passenger.onlineState }}</span></td>
              <td>{{ formatDate(passenger.lastSeen) }}</td>
              <td>
                <a *ngIf="passenger.profileId" [routerLink]="['/dashboard/passengers/profiles', passenger.profileId]" (click)="$event.stopPropagation()">
                  {{ passenger.profileId }}
                </a>
                <span *ngIf="!passenger.profileId">Unlinked</span>
              </td>
              <td>
                <button class="btn-icon-text" type="button" [disabled]="isSyncing(passenger.id)" (click)="syncProfile(passenger, $event)">
                  <i class="material-icons">{{ isSyncing(passenger.id) ? 'sync' : 'person_add' }}</i>
                  <span>{{ isSyncing(passenger.id) ? 'Syncing' : 'Sync profile' }}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-bar compact-pagination" *ngIf="!isLoadingPassengers && filteredPassengers.length > passengerPageSize">
        <button class="btn-icon-text" type="button" [disabled]="passengerPage === 1" (click)="changePassengerPage(passengerPage - 1)">
          <i class="material-icons">chevron_left</i>
          <span>Previous</span>
        </button>
        <span>Page {{ passengerPage }} of {{ getPassengerTotalPages() }}</span>
        <button class="btn-icon-text" type="button" [disabled]="passengerPage === getPassengerTotalPages()" (click)="changePassengerPage(passengerPage + 1)">
          <span>Next</span>
          <i class="material-icons">chevron_right</i>
        </button>
      </div>

      <ng-template #noPassengers>
        <div class="empty-state" *ngIf="!isLoadingPassengers && !passengerListError">
          <i class="material-icons">wifi_tethering_off</i>
          <h3>No hotspot passengers yet</h3>
          <p>Hotspot passenger records will appear here once provider data is available.</p>
        </div>
      </ng-template>
    </section>

    <aside class="detail-panel" *ngIf="selectedPassenger; else noSelection">
      <div class="detail-header">
        <div>
          <p class="eyebrow">Passenger detail</p>
          <h3>{{ selectedPassenger.displayName }}</h3>
          <span class="record-id">{{ selectedPassenger.provider }} {{ selectedPassenger.externalId }}</span>
        </div>
        <button class="primary-action" type="button" [disabled]="isSyncing(selectedPassenger.id)" (click)="syncProfile(selectedPassenger)">
          <i class="material-icons">{{ isSyncing(selectedPassenger.id) ? 'sync' : 'person_add_alt_1' }}</i>
          <span>{{ isSyncing(selectedPassenger.id) ? 'Syncing...' : 'Sync to passenger profile' }}</span>
        </button>
      </div>

      <div class="inline-error detail-error" *ngIf="syncErrors[selectedPassenger.id]">{{ syncErrors[selectedPassenger.id] }}</div>

      <dl class="detail-grid">
        <div>
          <dt>Provider</dt>
          <dd><span class="status-badge provider">{{ selectedPassenger.provider }}</span></dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd><span class="status-badge">{{ selectedPassenger.status }}</span></dd>
        </div>
        <div>
          <dt>Online state</dt>
          <dd><span class="status-badge" [ngClass]="'online-' + selectedPassenger.onlineState.toLowerCase()">{{ selectedPassenger.onlineState }}</span></dd>
        </div>
        <div>
          <dt>Package</dt>
          <dd>{{ selectedPassenger.packageName || '-' }}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{{ selectedPassenger.phone || '-' }}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{{ selectedPassenger.email || '-' }}</dd>
        </div>
        <div>
          <dt>First seen</dt>
          <dd>{{ formatDate(selectedPassenger.firstSeen) }}</dd>
        </div>
        <div>
          <dt>Last synced</dt>
          <dd>{{ formatDate(selectedPassenger.lastSynced) }}</dd>
        </div>
      </dl>

      <section class="sync-result" *ngIf="syncResults[selectedPassenger.id] as result">
        <div class="result-header">
          <div>
            <p class="eyebrow">Sync result</p>
            <h3>{{ result.profile.displayName }}</h3>
          </div>
          <div class="result-badges">
            <span class="status-badge provider">{{ result.externalIdentity.provider }}</span>
            <span class="status-badge" [ngClass]="getStatusLabel(result)">{{ getStatusLabel(result) }}</span>
            <span class="status-badge linked" *ngIf="result.createdExternalIdentity">linked</span>
          </div>
        </div>
        <dl class="result-grid">
          <div>
            <dt>Profile ID</dt>
            <dd>
              <a [routerLink]="['/dashboard/passengers/profiles', result.profile.id]">{{ result.profile.id }}</a>
            </dd>
          </div>
          <div>
            <dt>External identity ID</dt>
            <dd>{{ result.externalIdentity.id }}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{{ result.profile.phone || '-' }}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{{ result.profile.email || '-' }}</dd>
          </div>
          <div>
            <dt>Source</dt>
            <dd><span class="status-badge">{{ result.profile.primarySource }}</span></dd>
          </div>
        </dl>
      </section>
    </aside>

    <ng-template #noSelection>
      <aside class="detail-panel">
        <div class="empty-state">
          <i class="material-icons">person_search</i>
          <h3>No hotspot passenger selected</h3>
          <p>Select a passenger record to inspect provider details and sync it to a local profile.</p>
        </div>
      </aside>
    </ng-template>
  </div>
</section>

<app-confirm-dialog
  *ngIf="pendingHotspotAction as action"
  [title]="action.title"
  [message]="action.message"
  [confirmLabel]="action.confirmLabel"
  tone="primary"
  (confirmed)="confirmHotspotAction()"
  (cancelled)="cancelHotspotAction()">
</app-confirm-dialog>
